import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Search, 
  Share2, 
  Target, 
  FileText, 
  Mail, 
  Code, 
  BarChart3,
  ChevronRight,
  Globe,
  Zap,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";

const services = [
  {
    icon: Search,
    title: "SEO & Search Marketing",
    description: "Dominate search results with data-driven SEO strategies that drive organic growth.",
    link: "/services#seo"
  },
  {
    icon: Share2,
    title: "Social Media Marketing",
    description: "Build meaningful connections with your audience across all social platforms.",
    link: "/services#social"
  },
  {
    icon: Target,
    title: "PPC Advertising",
    description: "Maximize ROI with targeted paid campaigns on Google, Meta, and beyond.",
    link: "/services#ppc"
  },
  {
    icon: FileText,
    title: "Content Marketing",
    description: "Create compelling content that engages, educates, and converts your audience.",
    link: "/services#content"
  },
  {
    icon: Mail,
    title: "Email Marketing",
    description: "Nurture leads and drive conversions with personalized email campaigns.",
    link: "/services#email"
  },
  {
    icon: Code,
    title: "Web Development",
    description: "Build fast, responsive websites optimized for performance and conversions.",
    link: "/services#web"
  },
  {
    icon: BarChart3,
    title: "Analytics & Reporting",
    description: "Make informed decisions with comprehensive data analysis and insights.",
    link: "/services#analytics"
  }
];

const features = [
  {
    icon: Globe,
    title: "Global Reach",
    description: "Serving clients domestically and internationally with localized strategies."
  },
  {
    icon: Zap,
    title: "Data-Driven",
    description: "Every decision backed by analytics and measurable results."
  },
  {
    icon: Users,
    title: "Dedicated Team",
    description: "Expert professionals committed to your brand's success."
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const Home = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-glow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-full">
                <span className="w-2 h-2 bg-[#4f46e5] rounded-full animate-pulse"></span>
                <span className="text-sm text-slate-600 font-medium">Digital Marketing Excellence</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                Transform Your <br />
                <span className="text-[#1e3a8a]">Digital Presence</span>
              </h1>
              
              <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
                We connect brands to the future through data-driven digital experiences. 
                From SEO to social media, PPC to content marketing—we deliver results that matter.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    data-testid="hero-cta-btn"
                    size="lg" 
                    className="bg-[#1e3a8a] hover:bg-[#172554] text-white px-8 btn-primary"
                  >
                    Start Your Journey
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    data-testid="hero-services-btn"
                    variant="outline" 
                    size="lg" 
                    className="border-slate-300 hover:bg-slate-50 px-8"
                  >
                    Explore Services
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#1e3a8a]/20 to-[#4f46e5]/20 rounded-3xl blur-2xl"></div>
                <img
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop"
                  alt="Digital Marketing Dashboard"
                  className="relative rounded-2xl shadow-2xl w-full"
                />
              </div>
              
              {/* Floating Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-lg p-6 border border-slate-100"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#4f46e5]/10 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-[#4f46e5]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Avg. Growth Rate</p>
                    <p className="text-2xl font-bold text-slate-900">+147%</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Strip */}
      <section className="bg-[#1e3a8a] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">{feature.title}</h3>
                  <p className="text-blue-200 text-sm">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="text-[#4f46e5] font-medium text-sm uppercase tracking-wider">Our Services</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Comprehensive Digital Solutions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From strategy to execution, we offer end-to-end digital marketing services 
              tailored to your unique business goals.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {services.map((service) => (
              <motion.div
                key={service.title}
                variants={itemVariants}
                className="service-card bg-white rounded-xl p-6 cursor-pointer group"
              >
                <div className="w-12 h-12 bg-[#4f46e5]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#4f46e5] transition-colors">
                  <service.icon className="w-6 h-6 text-[#4f46e5] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{service.title}</h3>
                <p className="text-slate-600 text-sm mb-4 leading-relaxed">{service.description}</p>
                <Link 
                  to={service.link} 
                  data-testid={`service-link-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="inline-flex items-center text-[#4f46e5] text-sm font-medium group-hover:underline"
                >
                  Learn More
                  <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center mt-12"
          >
            <Link to="/services">
              <Button 
                data-testid="view-all-services-btn"
                variant="outline" 
                size="lg" 
                className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white px-8"
              >
                View All Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative bg-[#1e3a8a] rounded-3xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a8a] to-[#4f46e5] opacity-90"></div>
            <div className="absolute inset-0">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            </div>
            
            <div className="relative px-8 py-16 sm:px-16 sm:py-20 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to Grow Your Business?
              </h2>
              <p className="text-blue-100 max-w-2xl mx-auto mb-8 text-lg">
                Let's discuss how our digital marketing expertise can help you 
                achieve your business goals. Get in touch with our team today.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/contact">
                  <Button 
                    data-testid="cta-contact-btn"
                    size="lg" 
                    className="bg-white text-[#1e3a8a] hover:bg-slate-100 px-8"
                  >
                    Schedule a Consultation
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/careers">
                  <Button 
                    data-testid="cta-careers-btn"
                    variant="outline" 
                    size="lg" 
                    className="border-white text-white hover:bg-white/10 px-8"
                  >
                    Join Our Team
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
